// Canvas 2D renderer. Works in world units; the camera fits the whole map on screen.
import { BULLET, COLOR, type Effects, LOOK_SLOTS, PAINT, REPLACES_BULLET, paintBody } from './looks.js';
import { drawColor, isColor, turretFill } from './colors.js';
import { type Parts, STANDARD_PARTS, bob, drawDetails, drawTurret, drawUnder, hullLine, hovers, hullPath, rock, skirtsPath, tracksPath } from './parts.js';
import { type Sight, drawFog, sees, sight } from './fog.js';
import { Particles, type Popup } from './particles.js';
import { CHALLENGE_BANNER_MS, drawChallengeDone } from './challenge.js';
import { BLESSED, DRONE, DroneFlight, DroneShots, RISING, ZOMBIE, ZOMBIE_COLOR, drawBeam, drawDrone, drawRising, drawZombie } from './underdog.js';
import { drawBadge } from './badges.js';
import { setListener } from './sound.js';
import { drawBubble } from './emotes.js';
import { AUTUMN, HedgeGaps, LeafSwirl, Ruts, SNOWFALL, SPRING, WINTER, drawBlind, drawCover, drawSmoke, inSmoke, puffColors, drawHeat, drawShimmer, drawHoles, drawPatches, drawWater, drawRain, drawSnowfall, drawSplashes, drawWind, floorColor, inFogBank, onPatch, underLeaves, WATER } from './seasons.js';
import { rng } from './theme.js';

export interface Constants {
  tankRadius: number;
  bulletRadius: number;
  barrelLength: number;
  tankStride: number;
  bulletStride: number;
  blastStride: number;
  pickupStride: number;
  markStride: number;
  pickupRadius: number;
  mineRadius: number;
  spotStride: number;
  holeRadius: number;
  droneSide: number;
  smokeRadius: number;
}

export interface Frame {
  worldWidth: number;
  worldHeight: number;
  walls: Float32Array; // per polygon: corner count, then x, y per corner
  barriers?: Float32Array; // standing barriers (power-up), layout as walls
  // stride: x, y, angle, hpFraction, alive, spread, held, active (power-up kind + 1, 0 = none),
  // afflictions (bits), mines left, 0..1 of: time left of the power-up in use, the six
  // afflictions (bits order), charge or magazine reload, poison; reactive armor layers left,
  // warm-up respawns (wrapping count), overheating charge (0 = not, else 1 + share of the way to
  // its self-shot), hidden, underdog (5); 0..1 of swapped steering, of swapped pedals, of the chill
  // and of burning
  tanks: Float32Array;
  bullets: Float32Array; // stride: id, x, y, flags (BULLET_*), owner
  blasts?: Float32Array; // stride: x, y, radius, age 0..1, shockwave 0/1
  ring?: number; // tank marked with a pulsing red ring (online: own tank during the countdown)
  zone?: Float32Array; // safe rectangle: min x, min y, max x, max y (empty = no zone)
  countdown?: number; // seconds until shooting is allowed (0 = go)
  names?: string[]; // player name per tank, drawn above it (online)
  pickups?: Float32Array; // stride: x, y, kind
  mines?: Float32Array; // stride: x, y, owner
  oil?: Float32Array; // oil, frost and fire spots, stride: x, y, radius, kind (0 oil, 1 frost, 2 fire)
  aimPaths?: Float32Array[]; // per tank: shot path x, y, ... while it has aim help
  you?: number; // online: own tank; other cloaked tanks are not drawn (spectator: -1)
  anonymous?: boolean; // online: other tanks grey and without names (lobby setting)
  fog?: number; // online: fog of war, 0 off, 1 obstacles cast shadows, 2 also a view cone (lobby setting)
  season?: number; // the round's season (`Season::to_u8`, 0 = none) and its effects (bits)
  terrainEffects?: number;
  terrain?: Float32Array; // ground patches: per patch kind, corner count, x, y per corner
  holes?: Float32Array; // holes in the ice: x, y per hole
  wind?: Float32Array; // wind's pull now: x, y (empty or missing: calm)
  raining?: boolean; // a spring shower is falling
  fogBanks?: Float32Array; // fog banks where they are now, layout as walls
  smoke?: Float32Array; // smoke clouds (power-up), stride: x, y, share of the time left
  emotes?: Float32Array; // speech bubbles, stride: tank, emote id (EMOTES), share of its time gone
  emotePreview?: number; // id of the own emote picked on the controller, not sent yet (-1/none: none)
  ghost?: Float32Array; // the first one out as a ghost: x, y, angle, kind, ok, share left, tank, its piece (corner count, x, y per corner)
  looks?: Uint16Array; // cosmetics, LOOK_SLOTS item ids per tank (online; missing = standard)
  loadouts?: Uint8Array; // match shop components, 4 bytes per tank (online; missing = standard)
  colors?: Uint8Array; // player colour per tank (online; missing = the tank index)
  teams?: Uint8Array; // team per tank (online; missing or empty = no teams, Design §12)
  badges?: Uint8Array; // rank badge per tank (`Member::rank`) in the turret: online, warm-up only
  effects?: Effects; // trails and destruction effects across frames
  bounty?: number; // online: tank with the bounty on it, drawn with a glow (-1 = none)
  left?: number; // CSS px kept free at the left edge (online: standings or members)
  right?: number; // … and at the right edge (online: lobby settings)
  /** The map with its border fills the window (touch screens in a match): no bar, sides or margin. */
  fill?: boolean;
  plainTurrets?: boolean; // local game: turrets keep the player colour under a colour look
  players?: number; // local game: tanks 0.. this many - 1 are the players at the keyboard
}

/** By kind (`sim::powerup::Kind`, wire order, Design §8); `good`: helps the holder (§8.1), else hits others (§8.2); `text` for the guide. */
export const POWERUPS = [
  { name: 'Repair', icon: '🔧', good: true, text: '+1 HP, right on pickup' },
  { name: 'Shield', icon: '🛡️', good: true, text: 'blocks the next hit (5 s)' },
  { name: 'Cloak', icon: '👻', good: true, text: 'invisible to opponents, ends when you shoot or get hit (3 s)' },
  { name: 'Mines', icon: '💣', good: true, text: 'drop 3 mines, invisible to opponents, they explode like Explosive ammo and hit everyone nearby' },
  { name: 'Turbo', icon: '🚀', good: true, text: 'double speed (3 s)' },
  { name: 'Aim assist', icon: '🎯', good: true, text: 'no spread and a trajectory preview (6 s)' },
  { name: 'Jammer', icon: '📡', good: false, text: 'much more spread (4 s)' },
  { name: 'EMP', icon: '⚡', good: false, text: 'double reload time (4 s)' },
  { name: 'Slowdown', icon: '🐌', good: false, text: 'half speed (4 s)' },
  { name: 'Oil', icon: '💧', good: false, text: 'oil slick behind you, whoever drives over it slides' },
  { name: 'Bubble', icon: '🔮', good: true, text: 'blocks all damage, even the zone (1 s)' },
  { name: 'Mirror', icon: '🔁', good: true, text: 'the next bullet that hits you flies back and becomes yours (3 s)' },
  { name: 'Overcharge', icon: '⚡', good: true, text: 'your next shot does double damage' },
  { name: 'Ghost bullet', icon: '🌀', good: true, text: 'bullets fly through one obstacle (5 s)' },
  { name: 'Wall', icon: '🧱', good: true, text: 'a piece of wall behind you, stops bullets and tanks (8 s)' },
  { name: 'Radar', icon: '🛰️', good: true, text: 'shows cloaked opponents and mines of others (5 s)' },
  { name: 'Homing bullet', icon: '🏹', good: true, text: 'slow bullet that steers toward the nearest opponent' },
  { name: 'Shockwave', icon: '⭕️', good: true, text: 'pushes tanks and bullets away, no damage' },
  { name: 'Vampire', icon: '🧛', good: true, text: 'each of your hits heals 0.5 HP (4 s)' },
  { name: 'Immobilize', icon: '⛓️', good: false, text: 'cannot drive, only turn (1 s)' },
  { name: 'Gun jam', icon: '🔒', good: false, text: 'cannot shoot (2 s)' },
  { name: 'Marked', icon: '📍', good: false, text: 'visible even when cloaked, hits give +1 bounty, at most 3 per round (6 s)' },
  { name: 'Swapped steering', icon: '↔️', good: false, text: 'left turns right and right turns left (3 s)' },
  { name: 'Swapped pedals', icon: '↕️', good: false, text: 'forward drives back and back drives forward (3 s)' },
  { name: 'Smoke grenade', icon: '💨', good: true, text: 'a smoke cloud around you: whoever is in it is hidden and sees out only close by, like in a fog bank (4 s)' },
];
export const GOOD_COLOR = '#2e9e3c';
export const BAD_COLOR = '#c8102e';
const EMOJI_FONT = '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", system-ui, sans-serif';
const SHIELD = 1;
const CLOAK = 2;
const MINES = 3;
const BUBBLE = 10;
const MIRROR = 11;
const RADAR = 15;
const MARKED = 32;
/** Afflictions bit → power-up kind, in bits order (as their time-left bars). */
const AFFLICTIONS: [number, number][] = [
  [1, 6],
  [2, 7],
  [4, 8],
  [8, 19],
  [16, 20],
  [32, 21],
  [64, 22],
  [128, 23],
];
/** Time left (0..1) of affliction `k` (bits order) of the tank at offset `o`: the first six
 * after the power-up's, the two added later at the end of the stride. */
const afflictionLeft = (t: Float32Array, o: number, k: number) => (k < 6 ? t[o + 11 + k] : t[o + 28 + k - 6]);
/** Bullet flag bits (`sim::game::BULLET_*`). */
const BULLET_EXPLOSIVE = 1;
const BULLET_BIG = 2;
const BULLET_GHOST = 4;
const BULLET_HOMING = 8;
const BULLET_STUCK = 16;
const BULLET_POISON = 32;
const BULLET_SHARD = 64;
const BULLET_STICKY = 128;
const BULLET_IMPACT = 256;
const BULLET_MAGNET = 512;
const BULLET_OIL = 1024;
const BULLET_FROST = 2048;
const BULLET_NAPALM = 4096;

/**
 * Negative power-up `bit` falling onto the tank at offset `o`, `t` 0..1 of the way: big from
 * high above it, gathering speed and shrinking onto its place at the tank's rear (as `drawTank`
 * puts it, with the afflictions it has).
 */
function drawFalling(ctx: CanvasRenderingContext2D, t: Float32Array, o: number, bit: number, p: number, r: number): void {
  const hit = AFFLICTIONS.filter(([b]) => t[o + 8] & b);
  const k = hit.findIndex(([b]) => b === bit);
  if (k < 0) return;
  const [lx, ly] = [-r * 0.62, (k - (hit.length - 1) / 2) * r * 0.6];
  const a = t[o + 2];
  const [x, y] = [t[o] + Math.cos(a) * lx - Math.sin(a) * ly, t[o + 1] + Math.sin(a) * lx + Math.cos(a) * ly];
  const q = p * p; // falling: ever faster
  ctx.save();
  ctx.globalAlpha = Math.min(1, p * 6);
  drawIcon(ctx, x, y - (1 - q) * r * 4, r * 0.3 * (1 + 2.5 * (1 - q)), hit[k][1]);
  ctx.restore();
}

/** Status text for the power-up of the tank at offset `o`, empty if none. */
export function itemInfo(t: Float32Array, o: number): string {
  const [held, active] = [t[o + 6], t[o + 7]];
  if (held) return POWERUPS[held - 1].name;
  if (active === MINES + 1) return `Mines ×${t[o + 9]}`;
  if (active) return `${POWERUPS[active - 1].name} active`;
  return '';
}

const MARGIN = 30; // world units around the map
const BORDER = 12; // world units, thickness of the border walls (sim arena::BORDER)
const RIVET_SPACING = 90; // world units between the rivets on the border
const STATUS_BAR_PX = 56; // screen space kept free for the status bar at the bottom, CSS --bar-h
/** 16 tank colours, no pastels: each stands out on the light floor and on the dark panels. */
const COLORS = [
  '#e6194b', '#3cb4ff', '#ffe119', '#3cd44b', '#f58231', '#b45af0', '#46f0f0', '#f032e6',
  '#bcf60c', '#ff8fc0', '#2a9d8f', '#5a6cff', '#c0b030', '#b5713a', '#7fffd4', '#f4f4f4',
];

/** Overheating charge cannon (sim): time from turning hot to its self-shot, and the blue-to-red fade. */
const OVERHEAT_MS = 3000;
const OVERHEAT_FADE_MS = 300;

/** One beat of the ring around the own tank in the countdown. */
const RING_PULSE_MS = 700;

/** Every other tank with the lobby setting „Anonyme Farben“. */
const ANONYMOUS_COLOR = '#8c8c8c';

export function playerColor(i: number): string {
  return COLORS[i % COLORS.length];
}

/** Team colours and names (Design §12), by team index. */
export const TEAM_COLORS = ['#d32f2f', '#1976d2', '#388e3c', '#f9a825'];
export const TEAM_NAMES = ['Red', 'Blue', 'Green', 'Yellow'];

/** Whether tank `i` is on your side: yourself or a teammate. */
function ally(f: Frame, i: number): boolean {
  if (i === f.you) return true;
  const teams = f.teams;
  return !!teams?.length && f.you !== undefined && f.you >= 0 && teams[i] === teams[f.you];
}

/** Track prints of tank `i`: none while dead, cloaked (Design §8) or out of sight `s`, faint when hovering; 0 also hides its muzzle flashes. */
function trackOpacity(f: Frame, c: Constants, i: number, s?: Sight): number {
  const o = i * c.tankStride;
  if (f.tanks[o + 4] !== 1 || f.tanks[o + 7] === CLOAK + 1 || !seen(f, c, i, s) || hiding(f, c, i)) return 0;
  return f.loadouts && hovers(f.loadouts.subarray(i * 4, i * 4 + 4)) ? 0.5 : 1;
}

/** Whether another tank `i` hides in tall grass (sent by the server; not the own one or a teammate's). */
function hiding(f: Frame, c: Constants, i: number): boolean {
  return !ally(f, i) && f.tanks[i * c.tankStride + 22] === 1;
}

/** Whether tank `i` is in sight `s` (the own one always). */
function seen(f: Frame, c: Constants, i: number, s?: Sight): boolean {
  const o = i * c.tankStride;
  return i === f.you || sees(s, f.tanks[o], f.tanks[o + 1], c.tankRadius);
}

/** Colour per tank in this frame; anonymous: everyone but you (and your team) in grey, so nobody can tell who is who. */
function tankColors(f: Frame): (i: number) => string {
  const color = (i: number) => playerColor(f.colors?.[i] ?? i);
  return f.anonymous ? (i) => (ally(f, i) ? color(i) : ANONYMOUS_COLOR) : color;
}

/** Where the map landed on screen, border included, in CSS px. */
export interface MapRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function draw(ctx: CanvasRenderingContext2D, f: Frame, c: Constants): MapRect {
  const canvas = ctx.canvas;
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(canvas.clientWidth * dpr);
  const h = Math.round(canvas.clientHeight * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }

  const now = performance.now();
  const color = tankColors(f);
  const s = sight(f, c);
  const holes = f.holes ?? new Float32Array();
  // Open water: holes in the ice, the brook and ponds (Bug User 2026-10-27: tanks blew up in
  // the brook and the pond, only holes counted).
  const inWater = (x: number, y: number) => {
    for (let k = 0; k < holes.length; k += 2) if (Math.hypot(x - holes[k], y - holes[k + 1]) < c.holeRadius) return true;
    return !!f.terrain && onPatch(f.terrain, WATER, x, y);
  };
  f.effects?.update(f.tanks, c.tankStride, f.bullets, c.bulletStride, f.looks, color, now, inWater);
  f.effects?.layTracks(f.tanks, c.tankStride, f.looks, (i) => trackOpacity(f, c, i, s), now);
  f.effects?.shots(f.tanks, c.tankStride, f.bullets, c.bulletStride, f.loadouts, c, (i) => trackOpacity(f, c, i, s) > 0, now);
  const marks = (i: number) => trackOpacity(f, c, i, s) > 0;
  ruts.lay(f.tanks, c.tankStride, f.terrain, marks, now);
  swirl.lay(f.tanks, c.tankStride, f.terrain, marks, now);
  hedgeGaps.lay(f.tanks, c.tankStride, f.terrain, marks, now);
  // Whose power-ups rise over them: yours online, the keyboard players' in the local game.
  const own = (i: number) => i === f.you || (f.you === undefined && i < (f.players ?? 0));
  const bitFrame = { tanks: f.tanks, tankStride: c.tankStride, bullets: f.bullets, width: f.worldWidth, height: f.worldHeight, zone: f.zone, own, tankRadius: c.tankRadius, pickups: f.pickups, pickupRadius: c.pickupRadius, blasts: f.blasts, blastStride: c.blastStride };
  // Sounds are heard from the own tank, else (watching, the local game) the middle of the map.
  const me = f.you !== undefined && f.you >= 0 && f.tanks[f.you * c.tankStride + 4] === 1 ? f.you * c.tankStride : -1;
  if (me >= 0) setListener(f.tanks[me], f.tanks[me + 1]);
  else setListener(f.worldWidth / 2, f.worldHeight / 2);
  particles.update(bitFrame, marks, color, now);
  // A negative power-up falling onto an own tank shows at its rear only once it has landed.
  const falling = particles.falling(now);
  let shown = f.tanks;
  if (falling.length) {
    shown = f.tanks.slice();
    for (const d of falling) shown[d.tank * c.tankStride + 8] &= ~d.bit;
  }

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h); // the page background shows around the map

  // Camera: fit map + margin above the status bar and between `left` and `right`, centred.
  const bar = f.fill ? 0 : STATUS_BAR_PX * dpr;
  const left = f.fill ? 0 : (f.left ?? 0) * dpr;
  const free = w - left - (f.fill ? 0 : (f.right ?? 0) * dpr);
  const margin = f.fill ? BORDER : MARGIN;
  const scale = Math.min(free / (f.worldWidth + 2 * margin), (h - bar) / (f.worldHeight + 2 * margin));
  const ox = left + (free - f.worldWidth * scale) / 2;
  const oy = (h - bar - f.worldHeight * scale) / 2;
  ctx.drawImage(ground(f, w, h, scale, ox, oy), 0, 0);
  ctx.setTransform(scale, 0, 0, scale, ox, oy);
  // The line settings the ground was drawn with, which the rest of the frame always had.
  ctx.lineJoin = 'round';
  ctx.lineWidth = 1;
  ctx.strokeStyle = '#0b0d08';

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, f.worldWidth, f.worldHeight);
  ctx.clip(); // the brook runs on past the edge
  drawWater(ctx, f.terrain, now);
  ctx.restore();
  if (f.holes?.length) drawHoles(ctx, f.holes, c.holeRadius, now);
  ruts.draw(ctx, c.tankRadius, now);
  hedgeGaps.draw(ctx, f.terrain, floorColor(f.season), c.tankRadius, now);
  drawSplashes(ctx, f.terrain, f.tanks, c.tankStride, c.tankRadius, now);
  drawHeat(ctx, f.terrain, now);
  if (f.barriers) drawPolygons(ctx, f.barriers, '#8a5a2b');
  f.effects?.drawTracks(ctx, c.tankRadius, f.looks, color, now);

  if (f.zone && f.zone.length === 4) drawZone(ctx, f);
  drawMarks(ctx, f, c, now, s);

  const n = f.tanks.length / c.tankStride;
  const radar = f.you !== undefined && f.you >= 0 && f.tanks[f.you * c.tankStride + 7] === RADAR + 1;
  // Anonymous rounds hide who leads (Design §9.3).
  if (!f.anonymous && f.bounty !== undefined && f.bounty >= 0 && f.bounty < n && seen(f, c, f.bounty, s)) drawBountyGlow(ctx, f, c, now);
  // Wrecks first, so living tanks are drawn on top; warm-up wrecks fade out.
  for (const w of f.effects?.fadingWrecks(now) ?? []) {
    ctx.save();
    ctx.globalAlpha = w.fade;
    const parts = f.loadouts?.subarray(w.tank * 4, w.tank * 4 + 4);
    drawTank(ctx, new Float32Array([w.x, w.y, w.angle, 0, 0]), 0, color(w.tank), c, false, undefined, 0, w.turret, parts, now);
    ctx.restore();
  }
  for (const k of f.effects?.sinkings(now) ?? []) {
    drawSinking(ctx, c, k.x, k.y, k.angle, f.loadouts?.subarray(k.tank * 4, k.tank * 4 + 4), k.share, color(k.tank), now);
  }
  // Living tanks drawn for this viewer: only they show their emotes (none from hiding).
  const drawn = new Set<number>();
  for (const alivePass of [false, true]) {
    for (let i = 0; i < n; i++) {
      const o = i * c.tankStride;
      if ((f.tanks[o + 4] === 1) !== alivePass) continue;
      if (alivePass && !seen(f, c, i, s)) continue; // fog of war; wrecks stay
      // Others in a fog bank or hiding in tall grass are not seen (Design §4.1); teammates
      // see-through (§12).
      const covered = alivePass && i !== f.you && (hidesIn(f, c, f.tanks[o], f.tanks[o + 1]) || f.tanks[o + 22] === 1);
      if (covered && !ally(f, i)) continue;
      // Gone under: drawn where it went down, below (also when it is back in the warm-up).
      if (!alivePass && f.effects?.sinking(i, now) !== undefined) continue;
      const cloaked = alivePass && f.tanks[o + 7] === CLOAK + 1;
      const marked = (f.tanks[o + 8] & MARKED) !== 0;
      if (cloaked && !marked && f.you !== undefined && f.you >= 0 && !ally(f, i)) {
        // Design §8: enemies see nothing, radar an outline.
        if (radar) drawOutline(ctx, f.tanks, o, c);
        continue;
      }
      if (alivePass && (!covered || i === f.you)) drawn.add(i);
      const turret = f.effects?.hasTurret(i);
      const name = f.anonymous && i !== f.you ? undefined : f.names?.[i];
      // A risen zombie: standard parts, rotten paint (underdog bonus).
      const zombie = alivePass && f.tanks[o + ZOMBIE] === 1;
      const parts = zombie ? undefined : f.loadouts?.subarray(i * 4, i * 4 + 4);
      const tilt = f.effects?.tilt(i) ?? 0;
      const paint = zombie ? 0 : f.looks?.[i * LOOK_SLOTS + PAINT];
      if (alivePass && f.tanks[o + BLESSED] === 1) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, f.worldWidth, f.worldHeight);
        ctx.clip(); // the beam comes down from the map's own sky
        drawBeam(ctx, f.tanks[o], f.tanks[o + 1], c.tankRadius, now);
        ctx.restore();
      }
      const look = zombie ? 0 : (f.looks?.[i * LOOK_SLOTS + COLOR] ?? 0);
      // Teams: the tracks in the team colour, also with anonymous colours (Design §12).
      const team = alivePass ? teamColor(f, i) : undefined;
      drawTank(ctx, shown, o, zombie ? ZOMBIE_COLOR : color(i), c, cloaked || covered, name, paint, turret, parts, now, true, tilt, look, !f.plainTurrets, team, team, zombie ? 0 : (f.badges?.[i] ?? 0));
      if (zombie) drawZombie(ctx, f.tanks[o], f.tanks[o + 1], f.tanks[o + 2], c.tankRadius, now);
      if (alivePass && f.tanks[o + DRONE] > 0) {
        const [dx, dy, da] = droneFlight.at(i, f.tanks, o, c.droneSide, now);
        drawDrone(ctx, dx, dy, da, c.tankRadius, now);
      }
    }
  }
  droneShots.update(f.tanks, c.tankStride, (i, o) => droneFlight.last(i, f.tanks, o, c.droneSide), now);
  droneShots.draw(ctx, now);

  drawShimmer(ctx, f.terrain, now);
  drawCover(ctx, f.terrain, f.fogBanks, now);
  if (f.smoke?.length) drawSmoke(ctx, f.smoke, c.smokeRadius, now);
  if (f.ghost?.length) drawGhost(ctx, f.ghost, c.tankRadius, now);
  const emotes = f.emotes ?? new Float32Array();
  for (let k = 0; k < emotes.length; k += 3) {
    const i = emotes[k];
    if (!drawn.has(i)) continue;
    drawBubble(ctx, f.tanks[i * c.tankStride], f.tanks[i * c.tankStride + 1], c.tankRadius, emotes[k + 1], emotes[k + 2], now);
  }
  if (f.emotePreview !== undefined && f.emotePreview >= 0 && f.you !== undefined && drawn.has(f.you)) {
    const o = f.you * c.tankStride;
    drawBubble(ctx, f.tanks[o], f.tanks[o + 1], c.tankRadius, f.emotePreview, 0.5, now, true);
  }
  swirl.draw(ctx, now);
  // In a fog bank or smoke you still see your own tank, and your teammates see-through.
  for (let i = 0; i < n && f.you !== undefined && f.you >= 0; i++) {
    const o = i * c.tankStride;
    if (!ally(f, i) || f.tanks[o + 4] !== 1 || !hidesIn(f, c, f.tanks[o], f.tanks[o + 1])) continue;
    const parts = f.loadouts?.subarray(i * 4, i * 4 + 4);
    const own = (kind: number) => f.looks?.[i * LOOK_SLOTS + kind] ?? 0;
    const name = f.anonymous && i !== f.you ? undefined : f.names?.[i];
    drawTank(ctx, shown, o, color(i), c, i !== f.you, name, own(PAINT), f.effects?.hasTurret(i), parts, now, true, 0, own(COLOR), true, teamColor(f, i), teamColor(f, i));
  }

  if (f.ring !== undefined && f.ring >= 0 && f.tanks[f.ring * c.tankStride + 4] === 1) {
    // Pulsing red, growing and shrinking, so the eye finds it at once.
    const o = f.ring * c.tankStride;
    const pulse = 0.5 + 0.5 * Math.sin((now / RING_PULSE_MS) * Math.PI * 2);
    ctx.beginPath();
    ctx.arc(f.tanks[o], f.tanks[o + 1], c.tankRadius * (1.5 + 2.5 * pulse), 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(230, 30, 30, ${0.55 + 0.45 * pulse})`;
    ctx.lineWidth = 6;
    ctx.stroke();
  }

  f.effects?.drawFlashes(ctx, c.tankRadius, f.looks, color, now);
  const bullets = s ? inSight(f.bullets, c.bulletStride, 1, s) : f.bullets;
  f.effects?.drawTrails(ctx, bullets, c.bulletStride, c.bulletRadius, f.looks, color, now);
  f.effects?.drawPuffs(ctx, c.bulletRadius, now, (x, y) => sees(s, x, y), (x, y) => puffColors(f.terrain, x, y));
  particles.draw(ctx, now, (x, y) => sees(s, x, y));
  particles.drawPopups(ctx, c.tankRadius, now);
  for (const r of particles.rising(now)) {
    ctx.save();
    ctx.globalAlpha = r.t < 0.7 ? 1 : (1 - r.t) / 0.3;
    drawIcon(ctx, r.x, r.y, c.pickupRadius * 0.9, r.kind);
    ctx.restore();
  }
  for (const d of falling) drawFalling(ctx, f.tanks, d.tank * c.tankStride, d.bit, d.t, c.tankRadius);
  for (let i = 0; i < bullets.length; i += c.bulletStride) {
    // Some looks replace a plain bullet; ammo with its own look keeps its dot on top.
    const plain = (bullets[i + 3] & ~BULLET_SHARD) === 0;
    if (plain && REPLACES_BULLET.has(f.looks?.[bullets[i + 4] * LOOK_SLOTS + BULLET] ?? 0)) continue;
    drawBullet(ctx, bullets[i + 1], bullets[i + 2], bullets[i + 3], c.bulletRadius, now);
  }

  // Explosions: a flash that grows to the blast radius and fades; shockwaves: a blue ring.
  const blasts = f.blasts ?? new Float32Array();
  for (let i = 0; i < blasts.length; i += c.blastStride) {
    const [x, y, r, age] = [blasts[i], blasts[i + 1], blasts[i + 2], blasts[i + 3]];
    if (blasts[i + 4] === 1) {
      ctx.beginPath();
      ctx.arc(x, y, r * (0.3 + 0.7 * age), 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(60, 140, 255, ${1 - age})`;
      ctx.lineWidth = 4;
      ctx.stroke();
      continue;
    }
    ctx.beginPath();
    ctx.arc(x, y, r * (0.6 + 0.4 * age), 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, ${Math.round(200 - 120 * age)}, 0, ${0.7 * (1 - age)})`;
    ctx.fill();
  }
  f.effects?.drawBursts(ctx, c, now);
  if (f.season === WINTER && (f.terrainEffects ?? 0) & SNOWFALL) drawSnowfall(ctx, f.worldWidth, f.worldHeight, now);
  drawWind(ctx, f.wind, f.season, f.worldWidth, f.worldHeight, now);
  if (f.raining) drawRain(ctx, f.worldWidth, f.worldHeight, now);
  if (f.you !== undefined && f.you >= 0) {
    const o = f.you * c.tankStride;
    // From inside a fog bank or smoke: only three tank lengths around (like the bots), the
    // rest white with fog or grey with smoke.
    const [x, y] = [f.tanks[o], f.tanks[o + 1]];
    if (f.tanks[o + 4] === 1 && hidesIn(f, c, x, y)) {
      const veil = inFogBank(f.fogBanks, x, y) ? FOG_VEIL : SMOKE_VEIL;
      drawBlind(ctx, x, y, 6 * c.tankRadius, f.worldWidth, f.worldHeight, veil);
    }
  }
  if (s) drawFog(ctx, s, f, w, h, scale, ox, oy);

  // A zombie about to rise when only one other is left, who thought the round was won: big in
  // the middle of the map, for everybody (underdog bonus).
  const rising = [...Array(n).keys()].filter((i) => (f.tanks[i * c.tankStride + RISING] ?? 0) > 0);
  const living = [...Array(n).keys()].filter((i) => f.tanks[i * c.tankStride + 4] === 1).length;
  if (rising.length && living === 1) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const size = Math.min(f.worldHeight * scale * 0.16, f.worldWidth * scale * 0.12);
    rising.forEach((i, k) => {
      const name = f.anonymous && i !== f.you ? undefined : f.names?.[i];
      const y = oy + (f.worldHeight * scale) / 2 + (k - (rising.length - 1) / 2) * size * 1.6;
      drawRising(ctx, ox + (f.worldWidth * scale) / 2, y, size, name, color(i), now);
    });
  }
  // The own daily challenge just done: a banner in the upper third (Wunsch User 2026-10-28).
  const sinceChallenge = now - challengeAt;
  if (sinceChallenge >= 0 && sinceChallenge < CHALLENGE_BANNER_MS) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const size = Math.min(f.worldHeight * scale * 0.09, f.worldWidth * scale * 0.05);
    drawChallengeDone(ctx, ox + (f.worldWidth * scale) / 2, oy + f.worldHeight * scale * 0.3, size, sinceChallenge / CHALLENGE_BANNER_MS, now);
  }
  if (f.countdown) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.font = `bold ${Math.round(h / 5)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 6 * dpr;
    ctx.strokeStyle = '#111c';
    ctx.strokeText(String(f.countdown), w / 2, h / 2);
    ctx.fillStyle = '#fff';
    ctx.fillText(String(f.countdown), w / 2, h / 2);
  }
  const b = BORDER * scale;
  return {
    x: (ox - b) / dpr,
    y: (oy - b) / dpr,
    width: (f.worldWidth * scale + 2 * b) / dpr,
    height: (f.worldHeight * scale + 2 * b) / dpr,
  };
}

/** The records of `a` (`stride` numbers, x and y from `at`) in sight `s`. */
function inSight(a: Float32Array, stride: number, at: number, s: Sight): Float32Array {
  const out: number[] = [];
  for (let i = 0; i < a.length; i += stride) {
    if (sees(s, a[i + at], a[i + at + 1])) for (let k = 0; k < stride; k++) out.push(a[i + k]);
  }
  return Float32Array.from(out);
}

/** Dead tank `i` going under in open water, `p` 0..1 of the way: shrinking, fading, bubbles. */
function drawSinking(ctx: CanvasRenderingContext2D, c: Constants, x: number, y: number, angle: number, parts: Uint8Array | undefined, p: number, color: string, now: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1 - 0.5 * p, 1 - 0.5 * p);
  ctx.translate(-x, -y);
  ctx.globalAlpha = 1 - p;
  // Dead in the pose: grey like a wreck while it goes under (Wunsch User 2026-10-27).
  drawTank(ctx, new Float32Array([x, y, angle, 0, 0]), 0, color, c, false, undefined, 0, true, parts, now, false);
  // Water closing over it.
  ctx.globalAlpha = 0.6 * p;
  ctx.beginPath();
  ctx.arc(x, y, c.tankRadius * 1.3, 0, Math.PI * 2);
  ctx.fillStyle = '#1d3f57';
  ctx.fill();
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = '#d7ecf7';
  ctx.lineWidth = 1.5;
  for (let k = 0; k < 5; k++) {
    const q = (p * 2 + k / 5) % 1;
    ctx.globalAlpha = 1 - q;
    ctx.beginPath();
    ctx.arc(x + Math.sin(k * 2.1 + now / 300) * c.tankRadius * 0.6, y - q * c.tankRadius, 1.5 + 2 * q, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** Ruts in deep snow, across frames. */
const ruts = new Ruts();
const particles = new Particles();
/** When the own player last did the daily challenge (`performance.now()`). */
let challengeAt = -Infinity;

/** Shows the banner for the daily challenge just done by the own player. */
export function showChallengeDone(): void {
  challengeAt = performance.now();
}

/** Points over tank `tank` (online: the own one), see [`Particles.popup`]. */
export function scorePopup(tank: number, text: string, style: Popup, color: string): void {
  particles.popup(tank, text, style, color, performance.now());
}
const swirl = new LeafSwirl();
const hedgeGaps = new HedgeGaps();

const droneShots = new DroneShots();
const droneFlight = new DroneFlight();

let groundLayer: { canvas: HTMLCanvasElement; key: unknown[] } | undefined;

/** Floor, obstacles and border for this camera, redrawn only when the map or the camera changed. */
function ground(f: Frame, w: number, h: number, scale: number, ox: number, oy: number): HTMLCanvasElement {
  const key = [f.walls, f.terrain, f.season, f.worldWidth, f.worldHeight, w, h, scale, ox, oy];
  if (groundLayer && key.every((v, i) => v === groundLayer!.key[i])) return groundLayer.canvas;
  const canvas = groundLayer?.canvas ?? document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(scale, 0, 0, scale, ox, oy);
  ctx.fillStyle = floorColor(f.season);
  ctx.fillRect(0, 0, f.worldWidth, f.worldHeight);
  if (f.terrain) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, f.worldWidth, f.worldHeight);
    ctx.clip(); // the brook runs off the map's edge
    drawPatches(ctx, f.terrain);
    ctx.restore();
  }

  ctx.fillStyle = obstaclePattern(ctx);
  ctx.strokeStyle = '#1c1c1c';
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  for (let i = 0; i < f.walls.length; ) {
    const corners = f.walls[i++];
    ctx.beginPath();
    for (let k = 0; k < corners; k++, i += 2) ctx.lineTo(f.walls[i], f.walls[i + 1]);
    ctx.closePath();
    ctx.fill();
    if (f.season) {
      // A dusting of snow, autumn's rust, spring's moss or summer's bleaching sun on the rocks.
      const pattern: CanvasRenderingContext2D['fillStyle'] = ctx.fillStyle;
      ctx.fillStyle =
        f.season === WINTER ? 'rgba(240, 246, 250, 0.35)' : f.season === AUTUMN ? 'rgba(120, 90, 40, 0.22)' : f.season === SPRING ? 'rgba(60, 120, 40, 0.2)' : 'rgba(230, 200, 140, 0.2)';
      ctx.fill();
      ctx.fillStyle = pattern;
    }
    ctx.stroke();
  }
  drawBorder(ctx, f.worldWidth, f.worldHeight);
  groundLayer = { canvas, key };
  return canvas;
}

const OBSTACLE_TILE = 160; // world units, the splinter pattern repeats this often
let obstacleTile: HTMLCanvasElement | undefined;
let obstacleFill: { ctx: CanvasRenderingContext2D; fill: CanvasPattern | string } | undefined;

/** Splinter camo in three greys with hard edges, the same on every load; world-fixed. */
function obstaclePattern(ctx: CanvasRenderingContext2D): CanvasPattern | string {
  if (obstacleFill?.ctx === ctx) return obstacleFill.fill;
  if (!obstacleTile) {
    const size = OBSTACLE_TILE;
    obstacleTile = document.createElement('canvas');
    obstacleTile.width = obstacleTile.height = size;
    const t = obstacleTile.getContext('2d')!;
    t.fillStyle = '#3a3a3a';
    t.fillRect(0, 0, size, size);
    const rand = rng(11);
    for (const [color, count] of [
      ['#4b4b4b', 9],
      ['#2a2a2a', 8],
    ] as const) {
      t.fillStyle = color;
      for (let n = 0; n < count; n++) {
        // A long thin splinter at a slant: four corners round a point.
        const [cx, cy] = [rand() * size, rand() * size];
        const a = 0.5 + rand() * 0.4; // all splinters run roughly the same way
        const [len, wid] = [18 + rand() * 30, 4 + rand() * 9];
        const [dx, dy, nx, ny] = [Math.cos(a) * len, Math.sin(a) * len, -Math.sin(a) * wid, Math.cos(a) * wid];
        const skew = rand() * 0.6 - 0.3;
        const pts = [
          [-dx, -dy],
          [nx * (1 + skew), ny * (1 + skew)],
          [dx, dy],
          [-nx * (1 - skew), -ny * (1 - skew)],
        ];
        // Copies round the tile make it seamless.
        for (const ox of [-size, 0, size]) {
          for (const oy of [-size, 0, size]) {
            t.beginPath();
            for (const [px, py] of pts) t.lineTo(cx + ox + px, cy + oy + py);
            t.closePath();
            t.fill();
          }
        }
      }
    }
  }
  obstacleFill = { ctx, fill: ctx.createPattern(obstacleTile, 'repeat') ?? '#3a3a3a' };
  return obstacleFill.fill;
}

/** The border walls as an olive plate with rivets, like the panels (Panzergrau). */
function drawBorder(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const b = BORDER;
  const frame = new Path2D();
  frame.rect(-b, -b, w + 2 * b, h + 2 * b);
  frame.rect(0, 0, w, h);
  const plate = ctx.createLinearGradient(0, -b, 0, h + b);
  plate.addColorStop(0, '#5f6a3f');
  plate.addColorStop(0.5, '#4b5532');
  plate.addColorStop(1, '#3a4226');
  ctx.fillStyle = plate;
  ctx.fill(frame, 'evenodd');
  ctx.lineWidth = 1;
  ctx.strokeStyle = '#ffffff30'; // lit outer edge
  ctx.strokeRect(-b + 0.5, -b + 0.5, w + 2 * b - 1, h + 2 * b - 1);
  ctx.strokeStyle = '#0b0d08';
  ctx.strokeRect(-b, -b, w + 2 * b, h + 2 * b);
  // The plate's shadow on the field.
  ctx.fillStyle = '#00000030';
  ctx.fillRect(0, 0, w, 3);
  ctx.fillRect(0, 3, 3, h - 3);
  ctx.strokeStyle = '#0b0d08';
  ctx.strokeRect(0, 0, w, h);

  // Rivets along the middle of the plate, one in every corner.
  const r = b * 0.22;
  const rivet = (x: number, y: number) => {
    const g = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 0, x, y, r);
    g.addColorStop(0, '#c7cdd1');
    g.addColorStop(0.5, '#737b81');
    g.addColorStop(1, '#2a2f33');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  };
  const along = (length: number, at: (d: number) => void) => {
    const n = Math.max(1, Math.round((length + b) / RIVET_SPACING));
    for (let k = 0; k <= n; k++) at(-b / 2 + (k * (length + b)) / n);
  };
  along(w, (x) => (rivet(x, -b / 2), rivet(x, h + b / 2)));
  along(h, (y) => (rivet(-b / 2, y), rivet(w + b / 2, y)));
}

function drawPolygons(ctx: CanvasRenderingContext2D, polys: Float32Array, fill: string): void {
  ctx.fillStyle = fill;
  for (let i = 0; i < polys.length; ) {
    const corners = polys[i++];
    ctx.beginPath();
    for (let k = 0; k < corners; k++, i += 2) ctx.lineTo(polys[i], polys[i + 1]);
    ctx.closePath();
    ctx.fill();
  }
}

/** Bullet look by its flags (`sim::game::Bullet::flags`). */
function drawBullet(ctx: CanvasRenderingContext2D, x: number, y: number, flags: number, r: number, now: number): void {
  let [scale, fill] = [1, '#111'];
  if (flags & BULLET_EXPLOSIVE) [scale, fill] = [1.4, '#d04a00'];
  if (flags & BULLET_STICKY) [scale, fill] = [1.3, '#7a4a1a'];
  if (flags & BULLET_POISON) fill = '#5aa02c';
  if (flags & BULLET_IMPACT) [scale, fill] = [1.5, '#4a4a4a'];
  if (flags & BULLET_MAGNET) fill = '#c0392b';
  if (flags & BULLET_OIL) [scale, fill] = [1.2, '#1a1a1a'];
  if (flags & BULLET_FROST) [scale, fill] = [1.2, '#9fd8ff'];
  if (flags & BULLET_NAPALM) [scale, fill] = [1.2, '#ff6a00'];
  if (flags & BULLET_HOMING) [scale, fill] = [1.3, '#8a2be2'];
  if (flags & BULLET_SHARD) scale = 0.6;
  if (flags & BULLET_BIG) {
    scale = 1.9;
    fill = '#ffcc00';
    ctx.beginPath();
    ctx.arc(x, y, r * 3, 0, Math.PI * 2);
    ctx.fillStyle = '#ffcc0055';
    ctx.fill();
  }
  ctx.save();
  if (flags & BULLET_GHOST) ctx.globalAlpha = 0.45;
  ctx.beginPath();
  ctx.arc(x, y, r * scale, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  if (flags & BULLET_MAGNET) {
    // Magnet: a silver half, like the poles of a bar magnet.
    ctx.beginPath();
    ctx.arc(x, y, r * scale, -Math.PI / 2, Math.PI / 2);
    ctx.fillStyle = '#c8ccd0';
    ctx.fill();
  }
  if (flags & (BULLET_OIL | BULLET_FROST | BULLET_NAPALM)) {
    // Oil: a gloss spot; frost: a white core; napalm: a yellow one.
    ctx.beginPath();
    ctx.arc(x - r * 0.35, y - r * 0.35, r * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = flags & BULLET_OIL ? '#ffffff99' : flags & BULLET_NAPALM ? '#ffe040' : '#fff';
    ctx.fill();
  }
  if (flags & BULLET_STUCK) {
    // Stuck: a blinking ring until it explodes.
    if (Math.floor(now / 150) % 2 === 0) {
      ctx.beginPath();
      ctx.arc(x, y, r * scale + 3, 0, Math.PI * 2);
      ctx.strokeStyle = '#e6194b';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Radar: dashed outline where a cloaked enemy is. */
function drawOutline(ctx: CanvasRenderingContext2D, t: Float32Array, o: number, c: Constants): void {
  ctx.save();
  ctx.beginPath();
  ctx.arc(t[o], t[o + 1], c.tankRadius, 0, Math.PI * 2);
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = '#1a6a9a';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

/** Soft golden glow, slowly pulsing, under the living tank with the bounty (not while cloaked). */
function drawBountyGlow(ctx: CanvasRenderingContext2D, f: Frame, c: Constants, now: number): void {
  const o = f.bounty! * c.tankStride;
  if (f.tanks[o + 4] !== 1 || f.tanks[o + 7] === CLOAK + 1) return;
  const [x, y] = [f.tanks[o], f.tanks[o + 1]];
  const r = c.tankRadius * 2.2;
  const alpha = 0.4 + 0.12 * Math.sin(now / 350);
  const glow = ctx.createRadialGradient(x, y, c.tankRadius * 0.6, x, y, r);
  glow.addColorStop(0, `rgba(255, 210, 60, ${alpha})`);
  glow.addColorStop(1, 'rgba(255, 210, 60, 0)');
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = glow;
  ctx.fill();
}

/** Team colour of tank `i`, `undefined` without teams. */
/**
 * The ghost of the first one out (Design §6.3): a see-through ghost that bobs, the outline of
 * the piece it would place (green where it may, red where not) and its seconds left.
 */
function drawGhost(ctx: CanvasRenderingContext2D, g: Float32Array, r: number, now: number): void {
  const [x, y, ok, left] = [g[0], g[1], g[4] === 1, g[5]];
  ctx.save();
  ctx.beginPath();
  const corners = g[7];
  for (let k = 0; k < corners; k++) ctx.lineTo(g[8 + 2 * k], g[9 + 2 * k]);
  ctx.closePath();
  ctx.fillStyle = ok ? '#3cd44b33' : '#e6194b33';
  ctx.fill();
  ctx.setLineDash([6, 4]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = ok ? '#3cd44b' : '#e6194b';
  ctx.stroke();
  ctx.setLineDash([]);
  const bob = Math.sin(now / 300) * 3;
  ctx.globalAlpha = 0.75;
  ctx.font = `${Math.round(r * 1.6)}px ${EMOJI_FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('👻', x, y + bob);
  ctx.globalAlpha = 1;
  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 3;
  const secs = `${Math.ceil(left * 10)} s`;
  ctx.strokeText(secs, x, y - r - 2 + bob);
  ctx.fillText(secs, x, y - r - 2 + bob);
  ctx.restore();
}

/** Colour (r, g, b) of what one sees from inside a fog bank and from inside smoke. */
const FOG_VEIL = '236, 240, 242';
const SMOKE_VEIL = '118, 120, 122';

/** Whether `x`, `y` lies in a fog bank or a smoke cloud: hidden from the others, blind to them. */
function hidesIn(f: Frame, c: Constants, x: number, y: number): boolean {
  return inFogBank(f.fogBanks, x, y) || inSmoke(f.smoke, c.smokeRadius, x, y);
}

function teamColor(f: Frame, i: number): string | undefined {
  return f.teams?.length ? TEAM_COLORS[f.teams[i]] : undefined;
}

/** Oil and frost spots, mines, power-ups on the map and aim help paths, all below the tanks. */
function drawMarks(ctx: CanvasRenderingContext2D, f: Frame, c: Constants, now: number, s?: Sight): void {
  const oil = f.oil ?? new Float32Array();
  if (oil.length) {
    // Big spots of oil, frost or napalm ammo reach over obstacles and the edge: they lie only beside
    // them, on the map. The clip costs, so only with a big spot.
    ctx.save();
    let big = false;
    for (let i = 0; i < oil.length; i += c.spotStride) big ||= oil[i + 2] > 2 * c.tankRadius;
    if (big) {
      ctx.beginPath();
      ctx.rect(0, 0, f.worldWidth, f.worldHeight);
      ctx.clip();
      ctx.beginPath();
      ctx.rect(0, 0, f.worldWidth, f.worldHeight);
      for (let i = 0; i < f.walls.length; ) {
        const corners = f.walls[i++];
        for (let k = 0; k < corners; k++, i += 2) (k ? ctx.lineTo : ctx.moveTo).call(ctx, f.walls[i], f.walls[i + 1]);
        ctx.closePath();
      }
      ctx.clip('evenodd');
    }
    // One path per kind and layer: overlapping spots fill as one. Oil dark; frost pale blue
    // on a slightly wider white layer, a rim of hoarfrost; fire red-orange with a flickering
    // yellow core. Layer: kind, share of the radius (fire: flickers), plus, fill.
    const layers: [number, number, number, string][] = [
      [0, 1, 0, '#14141466'],
      [1, 1, 3, '#ffffffaa'],
      [1, 1, 0, '#8fd0ff80'],
      [2, 1, 1, '#e0400070'],
      [2, 0.55, 0, '#ffc23090'],
    ];
    for (const [kind, share, wider, fill] of layers) {
      ctx.beginPath();
      for (let i = 0; i < oil.length; i += c.spotStride) {
        if (oil[i + 3] !== kind) continue;
        const flicker = share < 1 ? 0.08 * Math.sin(now / 90 + oil[i] * 0.37) : 0;
        const r = oil[i + 2] * (share + flicker) + wider;
        ctx.moveTo(oil[i] + r, oil[i + 1]);
        ctx.arc(oil[i], oil[i + 1], r, 0, Math.PI * 2);
      }
      ctx.fillStyle = fill;
      ctx.fill();
    }
    ctx.restore();
  }

  const mines = f.mines ?? new Float32Array();
  for (let i = 0; i < mines.length; i += c.markStride) {
    // Fog of war; leaves hide foreign mines even from radar.
    if (!ally(f, mines[i + 2]) && (!sees(s, mines[i], mines[i + 1]) || underLeaves(f.terrain, mines[i], mines[i + 1]))) continue;
    ctx.beginPath();
    ctx.arc(mines[i], mines[i + 1], c.mineRadius, 0, Math.PI * 2);
    ctx.fillStyle = '#333';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(mines[i], mines[i + 1], c.mineRadius * 0.45, 0, Math.PI * 2);
    ctx.fillStyle = tankColors(f)(mines[i + 2]);
    ctx.fill();
  }

  const pickups = f.pickups ?? new Float32Array();
  for (let i = 0; i < pickups.length; i += c.pickupStride) {
    drawIcon(ctx, pickups[i], pickups[i + 1], c.pickupRadius, pickups[i + 2]);
    drawGlint(ctx, pickups[i], pickups[i + 1], c.pickupRadius, POWERUPS[pickups[i + 2]]?.good ?? true, now);
  }

  ctx.save();
  ctx.setLineDash([6, 6]);
  ctx.lineWidth = 1.5;
  (f.aimPaths ?? []).forEach((path, tank) => {
    if (!path || path.length < 4) return;
    ctx.beginPath();
    for (let i = 0; i < path.length; i += 2) ctx.lineTo(path[i], path[i + 1]);
    ctx.strokeStyle = tankColors(f)(tank);
    ctx.stroke();
  });
  ctx.restore();
}

/** Round power-up symbol: green or red disc with an emoji; `label` replaces it (mines left). */
function drawIcon(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, kind: number, label?: string): void {
  const p = POWERUPS[kind];
  if (!p) return;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = p.good ? GOOD_COLOR : BAD_COLOR;
  ctx.fill();
  ctx.lineWidth = Math.max(1, r * 0.18);
  ctx.strokeStyle = '#fff';
  ctx.stroke();
  ctx.fillStyle = '#fff';
  if (label) fillCentred(ctx, label, 'bold', 'system-ui, sans-serif', r * 1.3, x, y);
  else fillCentred(ctx, p.icon, '', EMOJI_FONT, r * 1.1, x, y);
}

const GLINT_MS = 2500; // a lying power-up glints once in this long
const GLINT_SHOW_MS = 450;

/**
 * A glint on a lying power-up now and then, each at its own time: a little four-pointed star,
 * pale green on good ones, pale red on bad ones.
 */
function drawGlint(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, good: boolean, now: number): void {
  const phase = (now + (x * 37 + y * 11)) % GLINT_MS;
  if (phase > GLINT_SHOW_MS) return;
  const k = Math.sin((phase / GLINT_SHOW_MS) * Math.PI);
  const [gx, gy, s] = [x + r * 0.55, y - r * 0.55, r * 0.55 * k];
  ctx.save();
  ctx.fillStyle = good ? '#eaffdc' : '#ffe0e0';
  ctx.globalAlpha = 0.9 * k;
  ctx.beginPath();
  ctx.moveTo(gx, gy - s);
  ctx.quadraticCurveTo(gx, gy, gx + s, gy);
  ctx.quadraticCurveTo(gx, gy, gx, gy + s);
  ctx.quadraticCurveTo(gx, gy, gx - s, gy);
  ctx.quadraticCurveTo(gx, gy, gx, gy - s);
  ctx.fill();
  ctx.restore();
}

/** Text drawn at this size and scaled down, so small sizes keep their shape and place. */
const GLYPH_PX = 100;
/** Per font and text at `GLYPH_PX`: where to draw it so its ink is centred on the origin. */
const inkOffsets = new Map<string, [number, number]>();

/**
 * `text` with its visible ink centred on `x`, `y`, `size` px high. Centring by the ink, not by
 * the advance and baseline: emoji fonts differ (Apple's sits far left in a centred advance), and
 * macOS places tiny canvas text badly, so it is drawn big and scaled.
 */
function fillCentred(ctx: CanvasRenderingContext2D, text: string, weight: string, family: string, size: number, x: number, y: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / GLYPH_PX, size / GLYPH_PX);
  ctx.font = `${weight} ${GLYPH_PX}px ${family}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  const key = `${ctx.font}|${text}`;
  let at = inkOffsets.get(key);
  if (!at) {
    const m = ctx.measureText(text);
    const [left, right] = [m.actualBoundingBoxLeft, m.actualBoundingBoxRight];
    const [up, down] = [m.actualBoundingBoxAscent, m.actualBoundingBoxDescent];
    at = right + left > 0 ? [(left - right) / 2, (up - down) / 2] : [-m.width / 2, GLYPH_PX * 0.35];
    inkOffsets.set(key, at);
  }
  ctx.fillText(text, at[0], at[1]);
  ctx.restore();
}

/** Darkens everything outside the safe zone and outlines its edge. */
function drawZone(ctx: CanvasRenderingContext2D, f: Frame): void {
  const [x0, y0, x1, y1] = f.zone!;
  if (x0 <= 0 && y0 <= 0 && x1 >= f.worldWidth && y1 >= f.worldHeight) return; // not shrinking yet
  ctx.beginPath();
  ctx.rect(0, 0, f.worldWidth, f.worldHeight);
  ctx.rect(x0, y0, x1 - x0, y1 - y0);
  ctx.fillStyle = '#b4141433';
  ctx.fill('evenodd');
  ctx.strokeStyle = '#d02020';
  ctx.lineWidth = 3;
  ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
}

/**
 * Tank at offset `o` of `t`; `paint`: its paint look (0 = plain); `turret`: false once it flew off;
 * `parts`: its match shop components, shown by their shapes (`parts.ts`).
 */
export function drawTank(
  ctx: CanvasRenderingContext2D,
  t: Float32Array,
  o: number,
  color: string,
  c: Pick<Constants, 'tankRadius' | 'barrelLength'>,
  cloaked: boolean,
  name?: string,
  paint = 0,
  turret = true,
  parts: Parts = STANDARD_PARTS,
  now = 0,
  bars = true,
  tilt = 0,
  colorLook = 0,
  colorTurret = true,
  nameColor = '#222',
  trackColor?: string,
  badge = 0,
): void {
  const [x, y, angle, hp, alive] = [t[o], t[o + 1], t[o + 2], t[o + 3], t[o + 4] === 1];
  const r = c.tankRadius;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.globalAlpha *= alive && !cloaked ? 1 : 0.35;
  if (alive) {
    drawUnder(ctx, parts, r, now);
    const s = bob(parts, now);
    ctx.scale(s, s);
  }

  // Everything that turns with the tank; tracks may draw it lifted at one end (`rock`).
  const body = (g: CanvasRenderingContext2D): void => {
    // Hull with tracks; a colour look (`colors.ts`) and a paint over it cover the tracks too,
    // they keep a black outline; tracks in a team colour (`trackColor`) stay so. The turret
    // keeps the player colour.
    const colored = alive && isColor(colorLook);
    const painted = alive && (paint > 0 || colored);
    const path = (shape: (g: CanvasRenderingContext2D, p: Parts, r: number) => void) => {
      g.beginPath();
      shape(g, parts, r);
    };
    // Heavy armor's skirts cover the tracks: in a team colour they show it, like the tracks.
    const hull = (g: CanvasRenderingContext2D, p: Parts, r: number) => hullPath(g, p, r, !trackColor);
    const looked = (g: CanvasRenderingContext2D) => {
      if (!trackColor) tracksPath(g, parts, r);
      hull(g, parts, r);
    };
    g.fillStyle = trackColor ?? (painted ? color : '#222');
    path(tracksPath);
    g.fill();
    g.fillStyle = alive ? color : '#555';
    path(hull);
    g.fill();
    if (trackColor) {
      g.fillStyle = trackColor;
      path(skirtsPath);
      g.fill();
    }
    g.strokeStyle = '#111';
    if (colored) {
      g.save();
      g.beginPath();
      looked(g);
      g.clip();
      drawColor(g, colorLook, r * 1.1, now);
      g.restore();
    }
    if (painted) {
      paintBody(
        g,
        paint,
        r,
        color,
        angle,
        looked,
        colored,
      );
    }
    if (painted || trackColor) {
      g.lineWidth = 1.5;
      path(tracksPath);
      g.stroke();
    }
    g.lineWidth = hullLine(parts);
    path(hullPath);
    g.stroke();
    drawDetails(g, parts, r, alive ? (t[o + 19] ?? 0) : 0);
    if (alive && t[o + 30] > 0) {
      // Chilled: hoarfrost over the hull.
      path(hullPath);
      g.fillStyle = '#d8f2ff55';
      g.fill();
      g.strokeStyle = '#eefaffd0';
      g.lineWidth = 2;
      g.stroke();
    }
    if (alive && t[o + 31] > 0) {
      // Burning: a flickering orange glow over the hull.
      path(hullPath);
      g.fillStyle = `rgba(255, 110, 0, ${0.25 + 0.15 * Math.sin(now / 70)})`;
      g.fill();
      g.strokeStyle = '#ffb000';
      g.lineWidth = 2;
      g.stroke();
    }

    g.strokeStyle = '#111';
    g.lineWidth = 2;
    // Overheating: from blue to red within OVERHEAT_FADE_MS, then pulsing.
    const since = alive && t[o + 21] >= 1 ? (t[o + 21] - 1) * OVERHEAT_MS : -1;
    const heat = since < 0 ? 0 : Math.min(1, since / OVERHEAT_FADE_MS);
    const pulse = 0.5 + 0.5 * Math.sin((now / 1000) * Math.PI * 4);
    // A flag goes over the turret as well (the colour of a pattern stays the player's).
    // The colour look goes over the turret as well (not in the local game: Red and Blue stay apart).
    const turretColor = (colored && colorTurret && turretFill(g, colorLook, r * 1.1, now)) || (alive ? color : '#555');
    if (turret) drawTurret(g, r, c.barrelLength, turretColor, parts, alive ? t[o + 17] : 0, heat, pulse);

    // Power-up held or in use in the turret, afflictions at the rear; both turn with the tank.
    if (alive) {
      const item = t[o + 6] || t[o + 7];
      if (item) drawIcon(g, 0, 0, r * 0.4, item - 1, t[o + 7] === MINES + 1 ? String(t[o + 9]) : undefined);
      else if (badge && turret) drawBadge(g, r * 0.4, badge, angle); // a held power-up goes first
      const hit = AFFLICTIONS.filter(([bit]) => t[o + 8] & bit);
      hit.forEach(([, kind], k) => drawIcon(g, -r * 0.62, (k - (hit.length - 1) / 2) * r * 0.6, r * 0.3, kind));
    }
  };
  if (alive) rock(ctx, parts, r, tilt, body);
  else body(ctx);
  ctx.restore();

  if (!alive) return;

  const ring = (radius: number, stroke: string, width: number, fill?: string): void => {
    ctx.beginPath();
    ctx.arc(x, y, r * radius, 0, Math.PI * 2);
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    ctx.strokeStyle = stroke;
    ctx.lineWidth = width;
    ctx.stroke();
  };
  if (t[o + 7] === SHIELD + 1) ring(1.35, '#2a7fd0cc', 3);
  if (t[o + 7] === BUBBLE + 1) ring(1.45, '#46f0f0', 3, '#46f0f044');
  if (t[o + 7] === MIRROR + 1) ring(1.35, '#c0c8d0', 3);
  if (t[o + 8] & MARKED) {
    ctx.beginPath();
    ctx.moveTo(x - r * 1.5, y);
    ctx.lineTo(x + r * 1.5, y);
    ctx.moveTo(x, y - r * 1.5);
    ctx.lineTo(x, y + r * 1.5);
    ctx.strokeStyle = '#c8102e';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // Bars above the tank, top down: HP (Design: never shown as a number), time left of the
  // power-up in use (blue), of each affliction (red), charge or magazine reload (yellow), poison (purple),
  // chill (pale blue), burning (orange).
  // `bars` false: none of them (victory pose).
  if (!bars) return;
  const shown: [number, string][] = [[hp, hp > 0.5 ? '#3cd44b' : hp > 0.25 ? '#ffb000' : '#e6194b']];
  if (t[o + 10] > 0) shown.push([t[o + 10], '#2a7fd0']);
  for (let k = 0; k < AFFLICTIONS.length; k++) if (afflictionLeft(t, o, k) > 0) shown.push([afflictionLeft(t, o, k), '#c8102e']);
  if (t[o + 17] > 0) shown.push([t[o + 17], '#ffd000']);
  if (t[o + 18] > 0) shown.push([t[o + 18], '#9b4dca']);
  if (t[o + 30] > 0) shown.push([t[o + 30], '#8fd8ff']);
  if (t[o + 31] > 0) shown.push([t[o + 31], '#ff7a1a']);
  const bw = r * 2.2;
  const by = y - r - 6 - (shown.length - 1) * 4;
  shown.forEach(([part, fill], k) => {
    const [top, height] = k ? [by + 1 + k * 4, 3] : [by, 4];
    ctx.fillStyle = '#000a';
    ctx.fillRect(x - bw / 2, top, bw, height);
    ctx.fillStyle = fill;
    ctx.fillRect(x - bw / 2, top, bw * part, height);
  });

  if (name) {
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = nameColor;
    ctx.fillText(name, x, by - 2);
  }
}
